import { useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Check } from "lucide-react";

const CostComparison = () => {
  const [plannerRate, setPlannerRate] = useState(200); // Average rate between $100-$300
  const [plannerHours, setPlannerHours] = useState(20); // Estimated monthly hours
  const [csoSalary] = useState(150000); // Annual CSO salary
  const monthlySubscription = 19.99;
  const lifetimePrice = 999;

  const monthlyPlannerCost = plannerRate * plannerHours;
  const monthlyCsoCost = csoSalary / 12;
  const annualSavingsVsPlanner = (monthlyPlannerCost * 12) - (monthlySubscription * 12);
  const annualSavingsVsCso = csoSalary - (monthlySubscription * 12);
  const lifetimeSavingsVsPlanner = (monthlyPlannerCost * 12 * 5) - lifetimePrice; // 5-year comparison
  const lifetimeSavingsVsCso = (csoSalary * 5) - lifetimePrice; // 5-year comparison

  const plannerSavingsPercentage = ((monthlyPlannerCost - monthlySubscription) / monthlyPlannerCost * 100).toFixed(1);
  const csoSavingsPercentage = ((monthlyCsoCost - monthlySubscription) / monthlyCsoCost * 100).toFixed(1);
  const lifetimePlannerSavingsPercentage = ((monthlyPlannerCost * 12 * 5 - lifetimePrice) / (monthlyPlannerCost * 12 * 5) * 100).toFixed(1);
  const lifetimeCsoSavingsPercentage = ((csoSalary * 5 - lifetimePrice) / (csoSalary * 5) * 100).toFixed(1);

  return (
    <div id="cost-comparison" className="relative py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            The numbers don't lie
          </h2>
          <p className="mt-4 text-xl text-gray-600">
            See how much you could save with our AI-powered strategic planning
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left side - Calculator */}
          <div className="space-y-8 p-8 rounded-2xl bg-white shadow-lg border border-gray-200">
            <h3 className="text-2xl font-semibold text-gray-900">Calculate Your Savings</h3>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm text-gray-600 mb-2">
                  Planner Rate ($/hour)
                </label>
                <div className="flex items-center gap-4">
                  <Slider
                    value={[plannerRate]}
                    onValueChange={(value) => setPlannerRate(value[0])}
                    min={100}
                    max={300}
                    step={10}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    value={plannerRate}
                    onChange={(e) => setPlannerRate(Number(e.target.value))}
                    className="w-24"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-2">
                  Monthly Hours Required
                </label>
                <div className="flex items-center gap-4">
                  <Slider
                    value={[plannerHours]}
                    onValueChange={(value) => setPlannerHours(value[0])}
                    min={5}
                    max={40}
                    step={1}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    value={plannerHours}
                    onChange={(e) => setPlannerHours(Number(e.target.value))}
                    className="w-24"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <div className="text-gray-600">Monthly Cost Breakdown:</div>
                <div className="mt-2 grid gap-2">
                  <div className="flex justify-between text-gray-900">
                    <span>Strategic Planner:</span>
                    <span>${monthlyPlannerCost.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-gray-900">
                    <span>CSO:</span>
                    <span>${monthlyCsoCost.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-gray-900 font-semibold">
                    <span>CSO Agent Monthly:</span>
                    <span>${monthlySubscription}</span>
                  </div>
                  <div className="flex justify-between text-gray-900 font-semibold">
                    <span>CSO Agent Lifetime:</span>
                    <span>${lifetimePrice}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Savings */}
          <div className="space-y-6">
            <div className="p-8 rounded-2xl bg-white shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-full bg-green-100 shrink-0">
                  <Check className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h4 className="text-2xl font-semibold text-gray-900">
                    Monthly Plan: Save ${annualSavingsVsPlanner.toLocaleString()} annually
                  </h4>
                  <p className="mt-2 text-lg text-green-600">
                    {plannerSavingsPercentage}% cheaper than hiring a strategic planner
                  </p>
                  <p className="mt-2 text-gray-600">
                    Get the same strategic guidance and planning expertise at a fraction of the cost
                  </p>
                </div>
              </div>
            </div>

            <div className="p-8 rounded-2xl bg-white shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-full bg-green-100 shrink-0">
                  <Check className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h4 className="text-2xl font-semibold text-gray-900">
                    Lifetime Plan: Save ${lifetimeSavingsVsPlanner.toLocaleString()} over 5 years
                  </h4>
                  <p className="mt-2 text-lg text-green-600">
                    {lifetimePlannerSavingsPercentage}% cheaper than hiring a strategic planner
                  </p>
                  <p className="mt-2 text-gray-600">
                    One-time payment for unlimited access to strategic planning tools and expertise
                  </p>
                </div>
              </div>
            </div>

            <div className="p-8 rounded-2xl bg-white shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-full bg-green-100 shrink-0">
                  <Check className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h4 className="text-2xl font-semibold text-gray-900">
                    Save ${annualSavingsVsCso.toLocaleString()} annually vs. CSO
                  </h4>
                  <p className="mt-2 text-lg text-green-600">
                    {csoSavingsPercentage}% cheaper than hiring a CSO
                  </p>
                  <p className="mt-2 text-gray-600">
                    Access C-level strategic expertise without the executive-level salary
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostComparison;
